import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import customFetch from '@/lib/customFetch.ts';
import { IResponse } from '@/model/IResponse';
import { ENVIRONMENT } from '@/environments/environment.ts';
import { IClientCulture } from '@/model/IClientCulture';

const urlGetResources = (id: number, system: string) => `${ENVIRONMENT.baseUrlIcarus}/culture/GetCultureStringResources?Id=${id}&System=${system}`;

// Create a simple event system to track translation loading
const translationEvents = {
  isLoading: true,
  callbacks: [] as Array<(isLoading: boolean) => void>,
  setLoading: (loading: boolean) => {
    translationEvents.isLoading = loading;
    translationEvents.callbacks.forEach(cb => cb(loading));
  },
  onLoadingChange: (callback: (isLoading: boolean) => void) => {
    translationEvents.callbacks.push(callback);
    return () => {
      translationEvents.callbacks = translationEvents.callbacks.filter(cb => cb !== callback);
    };
  }
};

export const isTranslationsLoading = () => translationEvents.isLoading;
export const onTranslationsLoadingChange = translationEvents.onLoadingChange;

export async function loadTranslations(cultureId: number, forceUpdate = false) {
  const expirationTime = localStorage.getItem('expirationTime');
  const savedCultureId = localStorage.getItem('cultureId');
  const currentTime = new Date().getTime();
  const translations = JSON.parse(localStorage.getItem('translations') || '{}');

  try {
    // Set loading state
    translationEvents.setLoading(true);

    if (translations &&
      expirationTime &&
      currentTime < Number.parseInt(expirationTime) &&
      parseInt(savedCultureId ?? '') == cultureId &&
      !forceUpdate
    ) {
      i18n.addResourceBundle(
        'culture',
        'translation',
        translations,
        true,
        true
      );
    } else {
      const loadedTranslations = await customFetch<IResponse<any>>(urlGetResources(cultureId, 'DVS Portal'));

      if (loadedTranslations) {
        i18n.addResourceBundle(
          'culture',
          'translation',
          loadedTranslations,
          true,
          true
        );

        localStorage.setItem(
          'translations',
          JSON.stringify(loadedTranslations)
        );

        localStorage.setItem(
          'expirationTime',
          String(new Date().getTime() + 60 * 1000 * 300)
        );

        localStorage.setItem('cultureId', cultureId.toString());
        const cultures: IClientCulture[] = JSON.parse(localStorage.getItem('cultures'));
        const selectedCultureCode = cultures.find(c => c.id === cultureId).name;
        localStorage.setItem('client-country-code', selectedCultureCode);
        window.dispatchEvent(new Event('storage'));
      }
      else{
        translationEvents.setLoading(false);
        return false;
      }
    }

    i18n.changeLanguage('culture');

    // Mark as not loading after translations are loaded
    translationEvents.setLoading(false);

    return true;
  } catch (error) {
    console.log('Error loading translations', error);
    // Mark as not loading even on error to prevent infinite loading state
    translationEvents.setLoading(false);
    return false;
  }
}

export const getSelectedCulture = (cultures: any[]) => {
  const storageCultureId = Number.parseInt(
    localStorage.getItem('cultureId') ?? '0'
  );

  let selectedCulture = null;
  selectedCulture = cultures?.find(
    (culture) => storageCultureId === culture.id
  );

  if (selectedCulture) return selectedCulture;

  return cultures.find((culture) => culture.isBase) || cultures[0];
};

i18n.use(initReactI18next).init({
  resources: {
    culture: { translation: {} },
  },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
  returnEmptyString: false,
  saveMissing: true,
  initImmediate: true,
});

export default i18n;
